/*
	ToDo: Update this component to use react-hook-form
	Task Name: [WEB] - Implement react-hook-form in ShareLoanUserForm
	Task Id: 10012697395
	Task Link: https://mortgage-automation-technologies.monday.com/boards/5842126081/pulses/10012697395
*/
import React, { useRef, useState, useImperativeHandle } from 'react'

import Box from '@mui/material/Box'
import { useIntl } from 'react-intl'
import AddIcon from '@mui/icons-material/Add'
import { LoanRole } from '@matech/thebigpos-sdk'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'

import { LoanUserRow } from '@/components/loanAppDetails/loan-users-table/types'
import { Button } from './Button'
import useUser from '../hooks/useUser'
import UsersSelect from './UsersSelect'
import FormControl from './formControls/FormControl'
import useRoleList from '../hooks/useRoleList'
import getAllowedRolesByUserRole, {
	LO_USER_ROLES_TO_LOAN_ROLE,
} from '../utils/rolePermissions'
import Select, { type SelectChangeEvent } from './formControls/Select'

type ValidateUserError = {
	userMessageKey?: string
	roleMessageKey?: string
}

export type HandleSubmitAddUserParams = {
	user: LoanUserRow
	role: LoanRole
}

export type ShareLoanUserRef = {
	setUserErrorMessageKey: (intlKey: string) => void
	setRoleErrorMessageKey: (intlKey: string) => void
	clearForm: () => void
	selectUsersFieldFocus: () => void
}

type ShareLoanUserFormProps = {
	handleSubmitAddUser?: (data: HandleSubmitAddUserParams) => void
	onValidateUserChange?: (user: LoanUserRow | null) => boolean
	disabled?: boolean
	loading?: boolean
	showRolesBasedOnUserRole?: boolean
	filterRolesUserField?: string[]
	loanUserFormRef?: React.Ref<ShareLoanUserRef>
}

const ShareLoanUserForm = ({
	loading,
	disabled,
	loanUserFormRef,
	showRolesBasedOnUserRole,
	filterRolesUserField,
	onValidateUserChange,
	handleSubmitAddUser,
}: ShareLoanUserFormProps) => {
	const [validationErrors, setValidationErrors] =
		useState<ValidateUserError>({})
	const [selectedLocalUser, setSelectedLocalUser] =
		useState<LoanUserRow | null>(null)
	const [selectedLocalRole, setSelectedLocalRole] =
		useState<LoanRole | null>(null)

	const autocompleteTextFieldRef = useRef<HTMLDivElement>(null)

	const { user } = useUser()
	const { formatMessage } = useIntl()

	const allowedRoles =
		showRolesBasedOnUserRole && selectedLocalUser?.role
			? getAllowedRolesByUserRole(selectedLocalUser.role)
			: undefined

	const { getRoleList } = useRoleList()

	const roleOptionList = getRoleList({
		includeRoles: allowedRoles,
	})

	const rolePlaceholder = formatMessage({ id: 'global.assign_role' })

	const selectUserError = !!validationErrors.userMessageKey

	const selectRoleError = !!validationErrors.roleMessageKey

	const selectUserErrorTextKey =
		validationErrors.userMessageKey ??
		'share_loan.search_and_select_user'

	const selectRoleErrorTextKey = validationErrors.roleMessageKey

	const disableRoleField = disabled || !selectedLocalUser

	const disabledAddButton =
		!selectedLocalUser ||
		!selectedLocalRole ||
		disabled ||
		loading ||
		selectUserError ||
		selectRoleError

	const clearForm = () => {
		setSelectedLocalUser(null)
		setSelectedLocalRole(null)
	}

	useImperativeHandle(loanUserFormRef, () => ({
		setUserErrorMessageKey: (intlKey: string) => {
			setValidationErrors((prev) => ({
				...prev,
				userMessageKey: intlKey,
			}))
		},
		setRoleErrorMessageKey: (intlKey: string) => {
			setValidationErrors((prev) => ({
				...prev,
				roleMessageKey: intlKey,
			}))
		},
		selectUsersFieldFocus: () =>
			autocompleteTextFieldRef.current?.focus(),
		clearForm,
	}))

	const determineSelectedRole = (
		user: LoanUserRow | null
	): LoanRole | null => {
		if (!user?.role) return null

		if (!showRolesBasedOnUserRole) {
			const roleOption = roleOptionList.find(
				(role) => role.value === user?.role
			)

			return roleOption?.value ?? null
		}

		const allowedRolesForUser = getAllowedRolesByUserRole(user.role)
		if (!allowedRolesForUser) return null

		const userRole = LO_USER_ROLES_TO_LOAN_ROLE[user.role]

		if (allowedRolesForUser.length === 0) {
			const availableRoles = roleOptionList.map((role) => role.value)
			return availableRoles.includes(userRole) ? userRole : null
		}

		return allowedRolesForUser.includes(userRole) ? userRole : null
	}

	const handleChangeUser = (value: LoanUserRow | null) => {
		if (onValidateUserChange && value) {
			const isUserAlreadyAdded = onValidateUserChange(value)

			if (isUserAlreadyAdded) {
				setValidationErrors((prev) => ({
					...prev,
					userMessageKey: 'share_loan.validation.user_already_added',
				}))
			}
		}

		setSelectedLocalUser(value)
		setSelectedLocalRole(determineSelectedRole(value))

		if (validationErrors.userMessageKey) {
			setValidationErrors((prev) => ({
				...prev,
				userMessageKey: undefined,
				duplicateMessageKey: undefined,
			}))
		}
	}

	const handleRoleSelectChange = (e: SelectChangeEvent<unknown>) => {
		const value = e.target.value as string

		setSelectedLocalRole(value as LoanRole)

		if (validationErrors.roleMessageKey) {
			setValidationErrors((prev) => ({
				...prev,
				roleMessageKey: undefined,
			}))
		}
	}

	const validateUserInput = () => {
		const errors: ValidateUserError = {}

		if (!selectedLocalUser) {
			errors.userMessageKey =
				'share_loan.validation.please_select_user'
		}

		if (selectedLocalUser?.id === user.id) {
			errors.userMessageKey =
				'share_loan.validation.cannot_select_self'
		}

		if (!selectedLocalRole) {
			errors.roleMessageKey =
				'share_loan.validation.please_select_role'
		}

		setValidationErrors(errors)

		return Object.keys(errors).length > 0
	}

	const handleAddUser = () => {
		const hasErrors = validateUserInput()

		if (hasErrors) return

		handleSubmitAddUser?.({
			user: selectedLocalUser!,
			role: selectedLocalRole!,
		})
	}

	return (
		<Box sx={{ display: 'flex', gap: 2 }}>
			<Box sx={{ flex: 2 }}>
				<UsersSelect
					value={selectedLocalUser}
					textFieldRef={autocompleteTextFieldRef}
					onOptionChange={handleChangeUser}
					disabled={disabled}
					error={selectUserError}
					filterRoles={filterRolesUserField}
					helperText={formatMessage({
						id: selectUserErrorTextKey,
					})}
				/>
			</Box>

			<Box sx={{ flex: 1 }}>
				<FormControl fullWidth required error={selectRoleError}>
					<Select
						label={formatMessage({
							id: 'global.assign_role',
						})}
						placeholder={rolePlaceholder}
						required
						disabled={disableRoleField}
						menuItems={roleOptionList}
						controlled={false}
						selectProps={{
							value: selectedLocalRole ?? '',
							onChange: handleRoleSelectChange,
						}}
					/>

					{/*
						ToDo: Remove this update the select component to use helperText and error
						Task Name: [WEB] - Update Select Component to handle helperText and error
						Task Id: 10011415408
						Task Link: https://mortgage-automation-technologies.monday.com/boards/5842126081/pulses/10011415408
    				*/}
					{selectRoleErrorTextKey && (
						<Typography variant="caption" color="error">
							{formatMessage({
								id: selectRoleErrorTextKey,
							})}
						</Typography>
					)}
				</FormControl>
			</Box>

			<Box sx={{ display: 'flex', alignItems: 'center' }}>
				<Button
					text={formatMessage({
						id: 'share_loan.add_user',
					})}
					onClick={handleAddUser}
					disabled={disabledAddButton}
					startIcon={
						loading ? <CircularProgress size={20} /> : <AddIcon />
					}
				/>
			</Box>
		</Box>
	)
}

export default ShareLoanUserForm
